import { Link } from 'react-router-dom';
import { MapPin, MessageCircle } from 'lucide-react';
import { FaFacebookF, FaLinkedinIn, FaTwitter, FaYoutube } from 'react-icons/fa';
import {
  COMPANY_ADDRESS,
  COMPANY_NAME,
  SITE_DOMAIN,
  SITE_NAME,
  SITE_URL,
} from '../constants/site';

const QUICK_LINKS = [
  { href: '/#services', label: 'Services' },
  { href: '/#guides', label: 'PC transfer guide' },
  { href: '/#features', label: 'Workspace tools' },
  { href: '/#issues', label: 'Common issues' },
  { href: '/#blog', label: 'Blog' },
  { to: '/blog', label: 'All articles' },
  { href: '/#contact', label: 'Contact' },
];

const LEGAL_LINKS = [
  { to: '/privacy-policy', label: 'Privacy Policy' },
  { to: '/terms-and-conditions', label: 'Terms & Conditions' },
  { to: '/refund-policy', label: 'Refund Policy' },
];

function openChat() {
  if (typeof window !== 'undefined' && window.$zoho?.salesiq?.floatwindow?.visible) {
    try {
      window.$zoho.salesiq.floatwindow.visible('show');
      return;
    } catch {
      /* ignore */
    }
  }
  window.location.href = '/#contact';
}

function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="s4h-footer" aria-label="Site footer">
      <div className="s4h-footer__glow" aria-hidden />

      <div className="s4h-footer__wrap">
        <div className="s4h-footer__hero">
          <div className="s4h-footer__brand">
            <Link to="/" className="s4h-footer__site">
              Support <span className="s4h-footer__site-accent">4</span> Help
            </Link>
            <p className="s4h-footer__tagline">
              Independent Family Tree Maker support—install, migrate, sync, and recover with clear steps via live chat.
            </p>
          </div>
          <button type="button" className="s4h-footer__chat" onClick={openChat}>
            <MessageCircle size={20} aria-hidden />
            <span>
              <small>Need help now?</small>
              Chat with support
            </span>
          </button>
        </div>

        <div className="s4h-footer__grid">
          <div className="s4h-footer__col">
            <h3>Quick links</h3>
            <ul>
              {QUICK_LINKS.map(({ href, to, label }) => (
                <li key={href || to}>
                  {to ? <Link to={to}>{label}</Link> : <a href={href}>{label}</a>}
                </li>
              ))}
            </ul>
          </div>

          <div className="s4h-footer__col">
            <h3>Contact</h3>
            <ul className="s4h-footer__contact-list">
              <li>
                <MapPin size={16} aria-hidden />
                <span>
                  <strong>{COMPANY_NAME}</strong>
                  <br />
                  {COMPANY_ADDRESS}
                </span>
              </li>
            </ul>
          </div>

          <div className="s4h-footer__col">
            <h3>Legal &amp; social</h3>
            <ul className="s4h-footer__legal">
              {LEGAL_LINKS.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to}>{label}</Link>
                </li>
              ))}
            </ul>
            <div className="s4h-footer__social" aria-label="Social media">
              <a href="https://facebook.com/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <FaFacebookF />
              </a>
              <a href="https://twitter.com/" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <FaTwitter />
              </a>
              <a href="https://youtube.com/" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <FaYoutube />
              </a>
              <a href="https://linkedin.com/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <FaLinkedinIn />
              </a>
            </div>
          </div>
        </div>

        <div className="s4h-footer__disclaimer">
          <p>
            <strong>Disclaimer:</strong> {SITE_NAME} provides educational guides and online support for genealogy
            software workflows. We are not affiliated with the publisher of Family Tree Maker. Always back up your tree
            and media before upgrades, merges, or file moves.
          </p>
        </div>

        <div className="s4h-footer__bar">
          <p>
            © {year}{' '}
            <a href={SITE_URL} target="_blank" rel="noopener noreferrer">
              {SITE_DOMAIN}
            </a>
            . Operated by {COMPANY_NAME}. All trademarks belong to their respective owners.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
